import {
  Bell,
  Check,
  Cloud,
  FileText,
  Globe,
  ListTodo,
  Moon,
  Search,
  ShieldCheck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal } from "../ui/Reveal";
import { ScribbleUnderline, StickerBadge } from "../ui/decorations";

/** Grid bento de funcionalidades: 2 cards grandes + 4 pequenos. */
export function FeaturesBento() {
  const { t } = useTranslation("landing");
  return (
    <section id="features" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal className="text-center mb-16">
          <StickerBadge className="border-ink/15 bg-cream text-ink mb-5" rotate={2}>
            {t("features.badge")}
          </StickerBadge>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold text-ink tracking-tight text-balance">
            {t("features.titlePrefix")}{" "}
            <ScribbleUnderline>{t("features.titleHighlight")}</ScribbleUnderline> {t("features.titleSuffix")}
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Grande: Editor (2 colunas) */}
          <Reveal className="md:col-span-2">
            <div className="group h-full p-8 rounded-3xl bg-cream border-2 border-ink/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-white border-2 border-ink/10 text-primary-600 mb-5">
                <FileText size={20} />
              </div>
              <h3 className="font-display text-xl font-bold text-ink mb-2">
                {t("features.editorTitle")}
              </h3>
              <p className="text-sm text-ink/70 leading-relaxed max-w-lg">
                {t("features.editorBody")}
              </p>
              {/* Mini-preview de formatação */}
              <div className="mt-6 flex flex-wrap gap-2" aria-hidden="true">
                {["B", "I", "H1", "</>", "❝", "☰"].map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center justify-center min-w-9 h-9 px-2 rounded-lg bg-white border-2 border-ink/10 text-xs font-bold text-ink/60 group-hover:border-primary-300 transition-colors"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Grande vertical: Tarefas + Kanban (laranja sólido, 2 linhas) */}
          <Reveal delay={80} className="md:row-span-2">
            <div className="h-full p-8 rounded-3xl bg-primary-500 border-2 border-ink/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex flex-col">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-white/90 text-primary-600 mb-5 self-start">
                <ListTodo size={20} />
              </div>
              <h3 className="font-display text-xl font-bold text-ink mb-2">
                {t("features.tasksTitle")}
              </h3>
              <p className="text-sm text-ink/80 leading-relaxed">
                {t("features.tasksBody")}
              </p>
              {/* Mini-Kanban decorativo */}
              <div className="mt-auto pt-8" aria-hidden="true">
                <div className="grid grid-cols-2 gap-2.5">
                  {/* Coluna: A fazer */}
                  <div className="rounded-xl bg-white/90 border-2 border-ink/10 p-2.5">
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-600" />
                      <span className="text-[10px] font-extrabold uppercase tracking-wide text-ink/60">{t("features.kanbanTodo")}</span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="rounded-lg bg-cream border border-ink/10 px-2 py-2 text-[10px] font-bold text-ink flex items-center gap-1.5">
                        {t("features.cardTodo1")}
                        <Bell size={10} className="ml-auto shrink-0 text-primary-700" />
                      </div>
                      {/* Card "sendo arrastado" rumo à coluna Feito */}
                      <div className="rounded-lg bg-white border border-ink/15 px-2 py-2 text-[10px] font-bold text-ink rotate-3 translate-x-3 shadow-lg">
                        {t("features.cardTodo2")}
                      </div>
                    </div>
                  </div>
                  {/* Coluna: Feito */}
                  <div className="rounded-xl bg-white/90 border-2 border-ink/10 p-2.5">
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-ink/60" />
                      <span className="text-[10px] font-extrabold uppercase tracking-wide text-ink/60">{t("features.kanbanDone")}</span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="rounded-lg bg-cream border border-ink/10 px-2 py-2 text-[10px] font-bold text-ink/50 line-through flex items-center gap-1.5">
                        {t("features.cardDone1")}
                        <Check size={10} strokeWidth={4} className="ml-auto shrink-0 text-primary-600" />
                      </div>
                      {/* Vaga esperando o card arrastado */}
                      <div className="rounded-lg border-2 border-dashed border-ink/20 px-2 py-2 text-[10px] font-bold text-ink/30 text-center">
                        {t("features.dropHere")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Pequenos */}
          {[
            {
              icon: Cloud,
              title: t("features.filesTitle"),
              description: t("features.filesBody"),
              delay: 0,
            },
            {
              icon: Globe,
              title: t("features.shareTitle"),
              description: t("features.shareBody"),
              delay: 80,
            },
            {
              icon: Search,
              title: t("features.searchTitle"),
              description: t("features.searchBody"),
              delay: 160,
            },
            {
              icon: Moon,
              title: t("features.themeTitle"),
              description: t("features.themeBody"),
              delay: 240,
            },
            {
              icon: ShieldCheck,
              title: t("features.securityTitle"),
              description: t("features.securityBody"),
              delay: 320,
            },
          ].map((f) => (
            <Reveal key={f.title} delay={f.delay}>
              <div className="h-full p-7 rounded-3xl bg-white border-2 border-ink/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary-300">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-cream text-primary-600 mb-4">
                  <f.icon size={18} />
                </div>
                <h3 className="font-display text-base font-bold text-ink mb-1.5">{f.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{f.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
